<template>
  <section class="TheCookiePolicy">
    <h2 class="TheCookiePolicy_Heading">Grove Collaborative Cookie Policy</h2>
    <p class="TheCookiePolicy_EffectiveDate">Effective Date: January 1, 2020</p>

    <PoliciesGrovePronounDisclaimer />

    <p>
      This cookie policy describes how and when we use cookies and related
      technologies. This cookie policy is incorporated into and is subject to
      <SkyLink inline to="#privacy-policy"
        >the Grove Collaborative privacy policy</SkyLink
      >. Capitalized terms used in this cookie policy but not defined have the
      meaning set forth in our Privacy Policy, which also includes additional
      details about the collection and use of information that we gather from
      you.
    </p>

    <h3 class="TheCookiePolicy_Subheading">What is a cookie?</h3>

    <p>
      Cookies are small text files sent by us to your computer or mobile device,
      which enables certain features and functionality of the Grove
      Collaborative Service. They are unique to your account or your browser. A
      cookie will typically contain the name of the domain from which the cookie
      has come, the “lifetime” of the cookie, and a randomly generated unique
      number or other value. Certain cookies will be used on our website
      regardless of whether you are logged in to your account or not. Session
      cookies last only while your browser is open and are automatically deleted
      when you close your browser. Persistent cookies last until you or your
      browser delete them or until they expire.
    </p>

    <h3 id="how-we-use-cookies" class="TheCookiePolicy_Subheading">
      Cookies used by Grove Collaborative
    </h3>
    <p>
      When you use the Service, we use cookies, web beacons, and other tracking
      technologies from third-party partners such as Google and Facebook for
      measurement services, better targeting ads, and for marketing purposes. We
      may use both session cookies and persistent cookies.
    </p>

    <table class="TheCookiePolicy_Table">
      <thead class="TheCookiePolicy_Table_Header">
        <tr class="TheCookiePolicy_Table_Row">
          <th class="TheCookiePolicy_Table_Heading">
            Category of Use of Cookies
          </th>
          <th class="TheCookiePolicy_Table_Heading">Description</th>
        </tr>
      </thead>
      <tr class="TheCookiePolicy_Table_Row">
        <td class="TheCookiePolicy_Table_Data">Authentication</td>
        <td class="TheCookiePolicy_Table_Data">
          When you sign in to the Service, cookies help us confirm your account
          and show you the right information and personalize your experience.
        </td>
      </tr>

      <tr class="TheCookiePolicy_Table_Row">
        <td class="TheCookiePolicy_Table_Data">
          Preferences, Features, Experience
        </td>
        <td class="TheCookiePolicy_Table_Data">
          We use cookies to gather certain information about you (such as
          browser type, server, language preference) about you in order to make
          your user experience more consistent and convenient.
        </td>
      </tr>
      <tr class="TheCookiePolicy_Table_Row">
        <td class="TheCookiePolicy_Table_Data">Marketing</td>
        <td class="TheCookiePolicy_Table_Data">
          Cookies and other ad technology such as beacons, pixels, and tags, as
          well as technologies from third parties such as Google and Facebook
          help us present you with more relevant content about our products,
          promotions, and our Service. They also help provide us with relevant
          research and reporting, and know when content has been shown to you.
        </td>
      </tr>
      <tr class="TheCookiePolicy_Table_Row">
        <td class="TheCookiePolicy_Table_Data">Performance and Analytics</td>
        <td class="TheCookiePolicy_Table_Data">
          Cookies or other technologies provide us with information about your
          use of our Service and help us learn how well our websites and Service
          perform. We also use cookies to understand, improve, and research
          products, features, and services, including to create logs and record
          when you access our Service from different devices. We may also use
          cookies to anonymize traffic and activity data we collect. We may also
          use information gathered from cookies to reduce or eliminate messages
          sent to you.
        </td>
      </tr>
      <tr class="TheCookiePolicy_Table_Row">
        <td class="TheCookiePolicy_Table_Data">Referral Tracking</td>
        <td class="TheCookiePolicy_Table_Data">
          We use cookies to identify and confirm what third party websites refer
          you to our Service.
        </td>
      </tr>
      <tr class="TheCookiePolicy_Table_Row">
        <td class="TheCookiePolicy_Table_Data">Security/Fraud Detection</td>
        <td class="TheCookiePolicy_Table_Data">
          We use cookies and other technologies to enable and support our
          security features, and to help us detect malicious activity.
        </td>
      </tr>
    </table>

    <h3 class="TheCookiePolicy_Subheading">Managing your Cookies</h3>
    <p>
      You may disable the use of cookies by modifying your browser settings.
      Please note that if you delete, or choose not to accept cookies from the
      Service, you may not be able to utilize the features of the Service to
      their fullest potential. For more information on cookies and how to
      disable cookies visit
      <SkyLink inline to="https://www.ftc.gov/ftc/cookies.shtm" target="_blank">
        www.ftc.gov/ftc/cookies.shtm
      </SkyLink>
      or
      <SkyLink
        inline
        to="https://www.usa.gov/optout_instructions.shtml"
        target="_blank"
      >
        https://www.usa.gov/optout_instructions.shtml </SkyLink
      >.
    </p>

    <h3 class="TheCookiePolicy_Subheading">Do Not Track</h3>
    <p>
      We do not currently respond to “do not track” signals or other mechanisms
      that might enable consumers to opt out of tracking on our Service.
    </p>

    <h3 id="third-party-cookies" class="TheCookiePolicy_Subheading">
      Third Party Cookies
    </h3>
    <p>
      In addition to the tracking technologies we place, other companies may set
      their own cookies or similar tools when you visit our Service. These
      companies include: third party analytics services, such as Google
      Analytics and Mixpanel
      <b>(“Analytics Services”)</b>; third party marketing websites, such as are
      Facebook, Google Adwords, Bing, Pinterest, Outbrain and Taboola; and
      payment processors such as Stripe. The information generated by the
      cookies or other technologies about your use of our Service from these
      third parties is used to compile reports on the use and activity of users
      on our website and service; to deliver advertising targeted to your
      interests and to better understand the usage and visitation of our Service
      and the other sites tracked by these third parties. By using the Service,
      you consent to the use of your information for analytical purposes. This
      policy does not apply to, and we are not responsible for the use of
      cookies or other tracking technologies by these third parties and we
      encourage you to check the privacy policies of advertisers and/or ad
      services to learn about their use of cookies and other technologies. If
      you would like more information about this practice and to know your
      choices about not having this information used by many of these companies,
      click here:
      <SkyLink
        inline
        to="https://www.aboutads.info/choices"
        target="_blank"
        tag="a"
      >
        http://www.aboutads.info/choices
      </SkyLink>
      or
      <SkyLink
        inline
        to="https://www.networkadvertising.org"
        target="_blank"
        tag="a"
      >
        http://www.networkadvertising.org </SkyLink
      >. The third parties mentioned above are not intended to be a complete
      list of third parties using cookies or other tracking technologies through
      our Service. For additional information, please contact us at
      <SkyLink inline to="mailto:support@grove.co">support@grove.co</SkyLink>.
    </p>
  </section>
</template>

<style lang="scss">
.TheCookiePolicy {
  display: grid;
  row-gap: var(--spacing-3x);

  &_Heading {
    @include type-brand-heading('2');
    margin-top: var(--spacing-8x);
  }

  &_Subheading {
    @include type-brand-heading('3');
    margin-top: var(--spacing-6x);
  }

  &_EffectiveDate {
    @include type-body('l', true);
    margin-bottom: var(--spacing-3x);
  }

  &_Table {
    border: 1px solid var(--border-color-default);
    border-collapse: collapse;

    &_Header {
      background-color: var(--surface-color-cream);
    }

    &_Heading,
    &_Data {
      @include type-body('s');

      text-align: left;
      padding: var(--spacing-4x) var(--spacing-2x);
    }

    &_Row:nth-child(even) {
      background-color: var(--surface-color-frost);
    }
  }
}
</style>
